"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { stageInBatches } from "@/lib/stage";
import { useFetch } from "@/lib/use-fetch";
import { Alert, Badge, Button, Card, Input, Modal, PageHeader, Textarea } from "@/components/ui";
import { SenderSelect } from "@/components/sender-select";
import { GroupPicker, TemplatePicker } from "@/components/pickers";
import { useToast } from "@/components/toast";
import { ConfirmRow, MessagePreview, SenderHeader } from "@/components/confirm-sms";
import { estimateSegments } from "@/lib/segments";
import { buildRateLookup, categorize } from "@/lib/sms-rates";
import { datetimeLocalMin } from "@/lib/datetime";
import { useDebounced } from "@/lib/use-debounced";
import { notifyAccountChanged } from "@/lib/account";
import { extractPlaceholders, parseCsv, renderTemplate } from "@/lib/template";
import type { Account, Contact, ContactGroup, SmsRate } from "@/lib/types";

const BATCH_SIZE = 2000;
// Staging requests are independent, so a few run at once. Kept small deliberately: this
// multiplies load on the gateway and the database pool behind it.
const STAGE_CONCURRENCY = 4;
const HARD_MAX = 100_000;
const JOB_TERMINAL = ["COMPLETED", "PARTIAL", "FAILED", "CANCELLED"];

// A column header -> a {{placeholder}} token (lower-case, safe charset).
function varKey(header: string): string {
  return header.trim().toLowerCase().replace(/[^a-z0-9_.]+/g, "_").replace(/^_+|_+$/g, "");
}

interface Recipient {
  destination: string;
  vars: Record<string, string>;
}

interface JobStatus {
  bulkJobId: number;
  campaignId: number | null;
  status: string;
  totalRecipients: number;
  enqueuedCount: number;
  failedReason: string | null;
}

export default function PersonalizedSmsPage() {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [sourceAddr, setSourceAddr] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [phoneCol, setPhoneCol] = useState(0);
  const [template, setTemplate] = useState("");

  const [schedule, setSchedule] = useState(false);
  const [scheduleLocal, setScheduleLocal] = useState("");

  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingScheduleAt, setPendingScheduleAt] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);

  const { data: ratesData } = useFetch<{ rates: SmsRate[] }>("sms/rates");
  const { data: accountData } = useFetch<Account>("account");
  const lookup = useMemo(() => (ratesData ? buildRateLookup(ratesData.rates) : null), [ratesData]);

  // Load a saved contact group as the recipient list. We shape it exactly like an uploaded CSV
  // (headers + rows), so every downstream feature — merge vars, preview, missing-value warnings,
  // credit estimate — works unchanged. A contact's attributes become the merge variables.
  const loadGroup = useCallback((contacts: Contact[], group: ContactGroup) => {
    const attrKeys: string[] = [];
    for (const c of contacts) {
      for (const k of Object.keys(c.attributes ?? {})) {
        if (!attrKeys.includes(k)) attrKeys.push(k);
      }
    }
    const hdr = ["phone", "name", ...attrKeys];
    const rows = contacts.map((c) => [
      c.msisdn,
      c.name ?? "",
      ...attrKeys.map((k) => c.attributes?.[k] ?? "")
    ]);

    setHeaders(hdr);
    setDataRows(rows);
    setPhoneCol(0);
    setFileName(`Group: ${group.name}`);
    setCampaignName((n) => n || group.name);
    toast.success(`Loaded ${contacts.length.toLocaleString()} contact(s) from "${group.name}".`);
  }, [toast]);

  // Column metadata: header + its placeholder token.
  const columns = useMemo(
    () => headers.map((h, i) => ({ index: i, header: h, key: varKey(h) || `col${i + 1}` })),
    [headers]
  );

  // Build recipients from the CSV: phone from the selected column, all columns become vars.
  const recipients = useMemo<Recipient[]>(() => {
    if (columns.length === 0) return [];
    const seen = new Set<string>();
    const out: Recipient[] = [];
    for (const row of dataRows) {
      const destination = (row[phoneCol] ?? "").trim();
      if (!destination || seen.has(destination)) continue;
      seen.add(destination);
      const vars: Record<string, string> = {};
      for (const c of columns) vars[c.key] = (row[c.index] ?? "").trim();
      out.push({ destination, vars });
    }
    return out;
  }, [dataRows, columns, phoneCol]);

  const count = recipients.length;
  const placeholders = useMemo(() => extractPlaceholders(template), [template]);
  const knownKeys = useMemo(() => new Set(columns.map((c) => c.key)), [columns]);
  const unknownPlaceholders = placeholders.filter((p) => !knownKeys.has(p));

  // The credit estimate and missing-data scan render EVERY recipient's template (up to HARD_MAX).
  // Keying them off the raw `template` re-ran that whole pass on every keystroke and froze the tab
  // on large lists. Debounce so the heavy work runs only after typing pauses; the live preview
  // (one recipient) stays keyed off `template` and updates instantly.
  const debouncedTemplate = useDebounced(template, 300);
  const debouncedPlaceholders = useMemo(() => extractPlaceholders(debouncedTemplate), [debouncedTemplate]);

  // Preview the first recipient's rendered message.
  const previewText = useMemo(
    () => (recipients[0] ? renderTemplate(template, recipients[0].vars) : template),
    [template, recipients]
  );

  // Rows that leave a used placeholder empty (missing data) — surfaced as a soft warning.
  const missingDataRows = useMemo(() => {
    if (recipients.length === 0 || debouncedPlaceholders.length === 0) return 0;
    let n = 0;
    for (const r of recipients) {
      if (debouncedPlaceholders.some((p) => !r.vars[p])) n++;
    }
    return n;
  }, [recipients, debouncedPlaceholders]);

  // Credit estimate: render each recipient, count its own segments, price by category.
  const estimate = useMemo(() => {
    if (!lookup || recipients.length === 0) return null;
    let credits = 0;
    let segMax = 0;
    let intl = 0;
    for (const r of recipients) {
      const text = renderTemplate(debouncedTemplate, r.vars);
      const seg = estimateSegments(text).segments || 1;
      segMax = Math.max(segMax, seg);
      const { category, creditsPerSms } = categorize(lookup, r.destination);
      if (category === "INTERNATIONAL") intl++;
      credits += seg * creditsPerSms;
    }
    return { credits, segMax, intl };
  }, [recipients, debouncedTemplate, lookup]);

  const blockedIntl = (estimate?.intl ?? 0) > 0 && accountData?.allowInternational === false;
  const sampleEncoding = estimateSegments(previewText).encoding;

  useEffect(() => {
    if (!jobId) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await api<JobStatus>(`sms/bulk/jobs/status?bulkJobId=${jobId}`);
        if (!active) return;
        setJobStatus(s);
        notifyAccountChanged(); // credit is drained as the job enqueues — refresh the sidebar
        if (JOB_TERMINAL.includes(s.status)) return;
      } catch {
        /* transient */
      }
      if (active) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [jobId]);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(String(reader.result ?? ""));
      if (rows.length < 2) {
        setError("The CSV needs a header row and at least one data row.");
        return;
      }
      const hdr = rows[0].map((h) => h.trim());
      const body = rows.slice(1);
      setHeaders(hdr);
      setDataRows(body);
      setFileName(file.name);
      // Guess the phone column by header name.
      const guess = hdr.findIndex((h) => /phone|msisdn|number|mobile|to|dest/i.test(h));
      setPhoneCol(guess >= 0 ? guess : 0);
      toast.success(`Loaded ${body.length.toLocaleString()} rows, ${hdr.length} columns from ${file.name}.`);
    };
    reader.onerror = () => setError("Could not read the file.");
    reader.readAsText(file);
    if (fileRef.current) fileRef.current.value = "";
  }

  function clearFile() {
    setHeaders([]);
    setDataRows([]);
    setFileName("");
    setPhoneCol(0);
  }

  function insertPlaceholder(key: string) {
    setTemplate((t) => `${t}{{${key}}}`);
  }

  function resolveScheduleAt(): string | undefined {
    if (!schedule) return undefined;
    if (!scheduleLocal) throw new Error("Pick a date and time to schedule.");
    const when = new Date(scheduleLocal);
    if (Number.isNaN(when.getTime())) throw new Error("Invalid schedule date.");
    if (when.getTime() <= Date.now()) throw new Error("Schedule time must be in the future.");
    return when.toISOString();
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (count === 0) {
      setError("Upload a CSV with recipients first.");
      return;
    }
    if (count > HARD_MAX) {
      setError(`Too many recipients (${count.toLocaleString()}). The maximum is ${HARD_MAX.toLocaleString()}.`);
      return;
    }
    if (!template.trim()) {
      setError("Write a message template.");
      return;
    }
    if (blockedIntl) {
      setError(`${estimate?.intl.toLocaleString()} recipient(s) are international, which your account is not permitted to send.`);
      return;
    }
    try {
      setPendingScheduleAt(resolveScheduleAt());
    } catch (err) {
      setError((err as Error).message);
      return;
    }
    setConfirmOpen(true);
  }

  async function doSend() {
    setConfirmOpen(false);
    const scheduleAt = pendingScheduleAt;
    const all = recipients;
    const total = all.length;

    setSending(true);
    setJobId(null);
    setJobStatus(null);
    setProgress({ sent: 0, total });

    try {
      const created = await api<{ bulkJobId: number }>("sms/bulk/jobs", {
        method: "POST",
        body: {
          sourceAddr,
          campaignName: campaignName.trim() || undefined,
          message: template,
          personalized: true,
          scheduleAt
        }
      });
      const newJobId = created.bulkJobId;

      // Uploaded with bounded concurrency instead of one-at-a-time: a 2M recipient list was
      // 1,000 strictly sequential round trips, which is minutes of waiting on this page.
      await stageInBatches({
        items: all,
        batchSize: BATCH_SIZE,
        concurrency: STAGE_CONCURRENCY,
        upload: (chunk) =>
          api("sms/bulk/jobs/recipients", {
            method: "POST",
            body: { bulkJobId: newJobId, recipients: chunk }
          }),
        onProgress: (n) => setProgress({ sent: n, total })
      });

      await api("sms/bulk/jobs/start", { method: "POST", body: { bulkJobId: newJobId } });

      setJobId(newJobId);
      toast.success(
        scheduleAt
          ? `${total.toLocaleString()} recipient(s) scheduled.`
          : `${total.toLocaleString()} personalized message(s) staged — sending in the background.`
      );

      // Reset inputs (keep sender).
      clearFile();
      setTemplate("");
      setCampaignName("");
      setSchedule(false);
      setScheduleLocal("");
      setPendingScheduleAt(undefined);
      setProgress(null);
    } catch (err) {
      setError((err as Error).message);
      toast.error((err as Error).message);
      setProgress(null);
    } finally {
      setSending(false);
    }
  }

  const pct = progress && progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0;
  const jobPct = jobStatus && jobStatus.totalRecipients > 0
    ? Math.round((jobStatus.enqueuedCount / jobStatus.totalRecipients) * 100)
    : 0;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Personalized SMS"
        subtitle="Upload a CSV and merge each recipient's data into the message with {{placeholders}}."
      />

      <form onSubmit={onSubmit}>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <SenderSelect value={sourceAddr} onChange={setSourceAddr} required />
                  <Input
                    label="Campaign name (optional)"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="July statements"
                  />
                </div>

                {/* CSV upload */}
                <div>
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-700">Recipients (CSV or contact group)</span>
                    <div className="flex items-center gap-3">
                      <GroupPicker onPick={loadGroup} />
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="text-xs font-medium text-brand-600 hover:underline"
                      >
                        Upload .csv
                      </button>
                    </div>
                  </div>

                  {headers.length > 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">{fileName}</p>
                          <p className="text-xs text-gray-500">
                            {count.toLocaleString()} unique recipient(s) · {headers.length} column(s)
                          </p>
                        </div>
                        <button type="button" onClick={clearFile} className="text-xs font-medium text-red-600 hover:underline">
                          Remove
                        </button>
                      </div>
                      <div className="mt-3">
                        <label className="mb-1 block text-xs font-medium text-gray-600">Phone number column</label>
                        <select
                          value={phoneCol}
                          onChange={(e) => setPhoneCol(Number(e.target.value))}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
                        >
                          {headers.map((h, i) => (
                            <option key={i} value={i}>
                              {h || `Column ${i + 1}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center">
                      <p className="text-sm text-gray-500">
                        Upload a CSV whose first row is column headers (e.g. <code>phone,name,balance</code>).
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        Each column becomes a <code>{"{{placeholder}}"}</code> you can insert into the message.
                      </p>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" />
                </div>

                {/* Placeholder chips */}
                {columns.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">Insert:</span>
                    {columns.map((c) => (
                      <button
                        key={c.index}
                        type="button"
                        onClick={() => insertPlaceholder(c.key)}
                        className="rounded-md border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300"
                      >
                        {`{{${c.key}}}`}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Message template</span>
                  <TemplatePicker onPick={setTemplate} />
                </div>
                <Textarea
                  rows={5}
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  placeholder="Hi {{name}}, your balance is {{balance}} credits. Thank you!"
                  required
                />

                {unknownPlaceholders.length > 0 && (
                  <p className="text-xs text-amber-600">
                    Unknown placeholder(s): {unknownPlaceholders.map((p) => `{{${p}}}`).join(", ")} — these render empty (no matching column).
                  </p>
                )}
                {missingDataRows > 0 && (
                  <p className="text-xs text-amber-600">
                    {missingDataRows.toLocaleString()} recipient(s) are missing a value used in the template (those spots render blank).
                  </p>
                )}

                {/* Scheduling */}
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="radio" checked={!schedule} onChange={() => setSchedule(false)} />
                      Send now
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="radio" checked={schedule} onChange={() => setSchedule(true)} />
                      Schedule for later
                    </label>
                    {schedule && (
                      <input
                        type="datetime-local"
                        value={scheduleLocal}
                        min={datetimeLocalMin()}
                        onChange={(e) => setScheduleLocal(e.target.value)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
                      />
                    )}
                  </div>
                </div>

                {progress && sending && (
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-gray-500">
                      <span>Staging recipients…</span>
                      <span>
                        {progress.sent.toLocaleString()} / {progress.total.toLocaleString()} ({pct}%)
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                      <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}

                {blockedIntl && (
                  <Alert>
                    {estimate?.intl.toLocaleString()} recipient(s) are international, which your account is not permitted to send.
                  </Alert>
                )}
                <Alert>{error}</Alert>
                <Button type="submit" size="lg" disabled={sending || !sourceAddr || count === 0 || blockedIntl}>
                  {sending ? "Sending…" : schedule ? "Review & Schedule" : "Review & Send"}
                </Button>
              </div>
            </Card>
          </div>

          {/* Summary + live preview */}
          <div className="space-y-6">
            <Card title="Preview" className="lg:sticky lg:top-6">
              {recipients[0] ? (
                <>
                  <p className="mb-2 text-xs text-gray-500">First recipient ({recipients[0].destination}):</p>
                  <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-800 dark:bg-gray-500/10">
                    {previewText || <span className="text-gray-400">Your message will appear here.</span>}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-400">Upload recipients to preview the merged message.</p>
              )}

              <dl className="mt-4 space-y-3 border-t border-gray-100 pt-4 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Recipients</dt>
                  <dd className="text-lg font-bold tabular-nums text-gray-900">{count.toLocaleString()}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Encoding</dt>
                  <dd><Badge tone="blue">{sampleEncoding}</Badge></dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">Max segments / msg</dt>
                  <dd className="font-medium tabular-nums text-gray-900">{estimate?.segMax ?? 0}</dd>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                  <dt className="text-gray-500">Est. total credits</dt>
                  <dd className="text-lg font-bold tabular-nums text-brand-600">
                    ~{(estimate?.credits ?? 0).toLocaleString()}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-500">When</dt>
                  <dd className="text-right font-medium text-gray-900">
                    {schedule ? (scheduleLocal ? new Date(scheduleLocal).toLocaleString() : "Scheduled") : "Send now"}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>
        </div>
      </form>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={pendingScheduleAt ? "Confirm scheduled campaign" : "Confirm personalized send"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={doSend}>{pendingScheduleAt ? "Confirm & Schedule" : "Confirm & Send"}</Button>
          </>
        }
      >
        <SenderHeader sender={sourceAddr} />
        <dl>
          <ConfirmRow label="Recipients">{count.toLocaleString()}</ConfirmRow>
          {campaignName.trim() && <ConfirmRow label="Campaign">{campaignName.trim()}</ConfirmRow>}
          <ConfirmRow label="Personalized">Yes — {placeholders.length} placeholder(s)</ConfirmRow>
          <ConfirmRow label="Max segments / msg">{estimate?.segMax ?? 0} ({sampleEncoding})</ConfirmRow>
          <ConfirmRow label="Est. credits">~{(estimate?.credits ?? 0).toLocaleString()}</ConfirmRow>
          <ConfirmRow label="When">
            {pendingScheduleAt ? new Date(pendingScheduleAt).toLocaleString() : "Send now"}
          </ConfirmRow>
        </dl>
        <p className="mt-2 mb-1 text-xs text-gray-500">Preview (first recipient):</p>
        <MessagePreview message={previewText} />
      </Modal>

      {jobStatus && (
        <div className="mt-6">
          <Card title={jobStatus.status === "QUEUED" ? "Campaign scheduled" : "Campaign started"}>
            <div className="mb-4">
              <div className="mb-1 flex justify-between text-xs text-gray-500">
                <span>{JOB_TERMINAL.includes(jobStatus.status) ? "Enqueued" : "Enqueuing"} to send queue</span>
                <span>
                  {jobStatus.enqueuedCount.toLocaleString()} / {jobStatus.totalRecipients.toLocaleString()} ({jobPct}%)
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full rounded-full transition-all ${jobStatus.status === "FAILED" ? "bg-red-500" : "bg-brand-500"}`}
                  style={{ width: `${jobPct}%` }}
                />
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-gray-500">Status</dt>
              <dd>
                <Badge tone={jobStatus.status === "FAILED" ? "red" : jobStatus.status === "COMPLETED" ? "green" : "blue"}>
                  {jobStatus.status}
                </Badge>
              </dd>
              <dt className="text-gray-500">Campaign</dt>
              <dd className="font-medium">{jobStatus.campaignId ? `#${jobStatus.campaignId}` : "pending…"}</dd>
            </dl>
            {jobStatus.failedReason && <p className="mt-3 text-xs text-red-600">Stopped early: {jobStatus.failedReason}</p>}
          </Card>
        </div>
      )}
    </div>
  );
}
